import { i18n } from '@lingui/core'
import { defineMessage } from '@lingui/macro'
import { useField } from 'formik'
import { useCallback, useEffect, useState } from 'react'
import ReactSelect from 'react-select'
import { useTheme } from 'styled-components'

import { NativePlatform, WebPlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { StSelectListContainer } from '@/modules/questionnaire/components/questionnaire-question/QuestionnaireQuestion.styled'
import { SelectListItem } from '@/modules/questionnaire/components/select-list-item'
import { BottomSheet } from '../../bottom-sheet/BottomSheet'
import { Padding } from '../../layout'
import { Input } from '../input'

import type { QuestionAnswerOption } from '@healthblocks-io/core'

type SelectProps = {
  id: string
  label?: string
  placeholder?: string
  options: QuestionAnswerOption[]
}

const selectFieldPlaceholder = defineMessage({
  id: 'select-field.placeholder',
  comment: 'Generic placeholder for the Select field.',
  message: 'Maak een keuze',
})

const Select = ({ id, label, placeholder, options }: SelectProps) => {
  const theme = useTheme()
  const [sheetVisible, setSheetVisible] = useState<boolean>(false)

  const [fieldValue, , helpers] = useField(id)

  // Get label value directly from Formik value - no local state needed
  const selectedLabel = options.find((option) => option.value === fieldValue.value)?.label

  const selectedOption = options.find((option) => option.value === fieldValue.value)

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    // Close sheet when value changes
    if (fieldValue.value && sheetVisible) {
      setSheetVisible(false)
    }
  }, [fieldValue.value, sheetVisible])

  const handleSheetOpen = useCallback(() => {
    setSheetVisible(true)
  }, [setSheetVisible])

  const handleSheetClose = useCallback(() => {
    setSheetVisible(false)
  }, [setSheetVisible])

  // Render Methods
  // ------------------------------------------------------------------------- /
  const renderSingleSelect = useCallback(() => {
    return (
      <StSelectListContainer>
        {options?.map((option) => (
          <SelectListItem
            type='radio'
            key={`${id}_${option.value}`}
            id={`${id}_${option.value}`}
            name={id}
            value={option.value}
            label={option.label}
          />
        ))}
      </StSelectListContainer>
    )
  }, [options, id])

  return (
    <>
      <NativePlatform>
        <Input
          name={id}
          label={label}
          type='text'
          placeholder={placeholder ?? i18n._(selectFieldPlaceholder)}
          value={selectedLabel ?? ''}
          noMargin
          fakeSelect
          onClick={handleSheetOpen}
        />

        <BottomSheet steps={[75, 100]} visible={sheetVisible} onClose={handleSheetClose}>
          <>{renderSingleSelect()}</>
        </BottomSheet>
      </NativePlatform>
      <WebPlatform>
        <Padding pb={theme.UI.Spacing.Space2}>
          <ReactSelect
            name={id}
            options={options.map((opt) => ({
              label: opt.label,
              value: opt.value,
            }))}
            value={selectedOption ? { label: selectedOption.label, value: selectedOption.value } : null}
            placeholder={i18n._(selectFieldPlaceholder)}
            onChange={(selectedOption) => helpers.setValue(selectedOption?.value ?? '')}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Padding>
      </WebPlatform>
    </>
  )
}

export { Select }
